import * as crypto from 'crypto'
import { nanoid } from 'nanoid'
import { AuthProvider, StorageAdapter, Session } from './types'

/**
 * OAuth state and PKCE code verifier storage interface
 */
export interface OAuthState {
  id: string
  state: string
  codeVerifier: string
  providerId: string
  redirectUri: string
  createdAt: Date
  expiresAt: Date
}

/**
 * OAuth authorization request parameters
 */
export interface OAuthAuthorizationRequest {
  clientId: string
  redirectUri: string
  scope: string[]
  state: string
  codeChallenge?: string
  codeChallengeMethod?: 'S256' | 'plain'
}

/**
 * OAuth token response from provider
 */
export interface OAuthTokenResponse {
  accessToken: string
  refreshToken?: string
  idToken?: string
  expiresIn: number
  tokenType: string
  scope?: string
}

/**
 * OAuth user profile from provider
 */
export interface OAuthProfile {
  sub: string // subject/unique identifier
  email?: string
  name?: string
  picture?: string
  emailVerified?: boolean
  metadata?: Record<string, any>
}

/**
 * OAuth provider configuration for known providers
 */
const PROVIDER_CONFIGS: Record<string, {
  authorizationUrl: string
  tokenUrl: string
  userInfoUrl: string
  defaultScopes: string[]
}> = {
  google: {
    authorizationUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    tokenUrl: 'https://oauth2.googleapis.com/token',
    userInfoUrl: 'https://openidconnect.googleapis.com/v1/userinfo',
    defaultScopes: ['openid', 'email', 'profile']
  },
  github: {
    authorizationUrl: 'https://github.com/login/oauth/authorize',
    tokenUrl: 'https://github.com/login/oauth/access_token',
    userInfoUrl: 'https://api.github.com/user',
    defaultScopes: ['user:email']
  },
  microsoft: {
    authorizationUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
    userInfoUrl: 'https://graph.microsoft.com/v1.0/me',
    defaultScopes: ['openid', 'email', 'profile']
  },
  discord: {
    authorizationUrl: 'https://discord.com/api/oauth2/authorize',
    tokenUrl: 'https://discord.com/api/oauth2/token',
    userInfoUrl: 'https://discord.com/api/users/@me',
    defaultScopes: ['identify', 'email']
  }
}

/**
 * PKCE (Proof Key for Public Clients) utilities
 */
export class PKCEGenerator {
  /**
   * Generate a PKCE code verifier (43-128 characters)
   */
  static generateCodeVerifier(): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
    const length = 128
    let result = ''

    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length))
    }

    return result
  }

  /**
   * Generate PKCE code challenge from code verifier
   * S256 = BASE64URL(SHA256(code_verifier))
   */
  static generateCodeChallenge(codeVerifier: string): string {
    const hash = crypto.createHash('sha256').update(codeVerifier).digest()
    return this.base64UrlEncode(hash)
  }

  /**
   * Verify PKCE code challenge
   */
  static verifyCodeChallenge(codeVerifier: string, codeChallenge: string): boolean {
    const calculatedChallenge = this.generateCodeChallenge(codeVerifier)
    return calculatedChallenge === codeChallenge
  }

  private static base64UrlEncode(buffer: Buffer): string {
    return buffer
      .toString('base64')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')
  }
}

/**
 * OAuth flow manager
 */
export class OAuthManager {
  private storage: StorageAdapter
  private stateMap: Map<string, OAuthState> = new Map()
  private stateTTL: number = 10 * 60 * 1000 // 10 minutes
  private cleanupTimer?: ReturnType<typeof setInterval>

  constructor(storage: StorageAdapter) {
    this.storage = storage
    // Cleanup expired states every minute
    this.cleanupTimer = setInterval(() => this.cleanupExpiredStates(), 60 * 1000)
  }

  /**
   * Close the manager and clear internal timers (useful for tests/cleanup)
   */
  close(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer)
      this.cleanupTimer = undefined
    }
  }

  /**
   * Generate authorization URL for OAuth flow
   */
  generateAuthorizationUrl(
    provider: AuthProvider,
    redirectUri: string,
    appUrl: string,
    usePKCE: boolean = true
  ): { url: string; state: string; codeVerifier?: string } {
    if (!provider.clientId) {
      throw new Error(`Provider ${provider.id} missing clientId`)
    }

    const providerConfig = PROVIDER_CONFIGS[provider.id]
    if (!providerConfig) {
      throw new Error(`Unknown OAuth provider: ${provider.id}`)
    }

    // Generate state for CSRF protection
    const state = crypto.randomBytes(32).toString('hex')
    const codeVerifier = usePKCE ? PKCEGenerator.generateCodeVerifier() : undefined
    const codeChallenge = codeVerifier ? PKCEGenerator.generateCodeChallenge(codeVerifier) : undefined

    // Store state and code verifier
    const oauthState: OAuthState = {
      id: nanoid(),
      state,
      codeVerifier: codeVerifier || '',
      providerId: provider.id,
      redirectUri,
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + this.stateTTL)
    }
    this.stateMap.set(state, oauthState)

    // Build authorization URL
    const params = new URLSearchParams({
      client_id: provider.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      state,
      scope: (provider.scopes || providerConfig.defaultScopes).join(
        provider.id === 'github' ? ',' : ' '
      )
    })

    if (codeChallenge) {
      params.append('code_challenge', codeChallenge)
      params.append('code_challenge_method', 'S256')
    }

    return {
      url: `${providerConfig.authorizationUrl}?${params.toString()}`,
      state,
      codeVerifier
    }
  }

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  async handleCallback(
    provider: AuthProvider,
    code: string,
    state: string,
    codeVerifier?: string
  ): Promise<{ tokens: OAuthTokenResponse; state: OAuthState }> {
    // Validate state
    const oauthState = this.stateMap.get(state)
    if (!oauthState) {
      throw new Error('Invalid or expired state parameter')
    }

    if (oauthState.providerId !== provider.id) {
      throw new Error('State provider mismatch')
    }

    if (oauthState.expiresAt < new Date()) {
      this.stateMap.delete(state)
      throw new Error('State has expired')
    }

    // Validate PKCE code verifier if used
    if (oauthState.codeVerifier && !codeVerifier) {
      throw new Error('Code verifier required for PKCE flow')
    }

    if (oauthState.codeVerifier && codeVerifier) {
      const isValid = PKCEGenerator.verifyCodeChallenge(codeVerifier, oauthState.codeVerifier)
      if (!isValid) {
        throw new Error('Invalid code verifier')
      }
    }

    // Exchange code for tokens
    const tokens = await this.exchangeCodeForTokens(provider, code, oauthState.redirectUri)

    // Clean up state
    this.stateMap.delete(state)

    return { tokens, state: oauthState }
  }

  /**
   * Exchange authorization code for access token
   */
  private async exchangeCodeForTokens(
    provider: AuthProvider,
    code: string,
    redirectUri: string
  ): Promise<OAuthTokenResponse> {
    if (!provider.clientId || !provider.clientSecret) {
      throw new Error(`Provider ${provider.id} missing credentials`)
    }

    const providerConfig = PROVIDER_CONFIGS[provider.id]
    if (!providerConfig) {
      throw new Error(`Unknown OAuth provider: ${provider.id}`)
    }

    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      client_id: provider.clientId,
      client_secret: provider.clientSecret,
      redirect_uri: redirectUri
    })

    const response = await fetch(providerConfig.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: params.toString()
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Token exchange failed: ${error}`)
    }

    const data = await response.json()

    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      idToken: data.id_token,
      expiresIn: data.expires_in || 3600,
      tokenType: data.token_type || 'Bearer',
      scope: data.scope
    }
  }

  /**
   * Get user profile from OAuth provider
   */
  async getUserProfile(
    provider: AuthProvider,
    accessToken: string
  ): Promise<OAuthProfile> {
    const providerConfig = PROVIDER_CONFIGS[provider.id]
    if (!providerConfig) {
      throw new Error(`Unknown OAuth provider: ${provider.id}`)
    }

    const response = await fetch(providerConfig.userInfoUrl, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch user profile: ${response.statusText}`)
    }

    const data = await response.json()

    // Normalize profile based on provider
    const profile = this.normalizeProfile(provider.id, data)

    return profile
  }

  /**
   * Normalize user profile from different OAuth providers
   */
  private normalizeProfile(providerId: string, data: any): OAuthProfile {
    switch (providerId) {
      case 'google':
        return {
          sub: data.sub,
          email: data.email,
          name: data.name,
          picture: data.picture,
          emailVerified: data.email_verified
        }

      case 'github':
        return {
          sub: String(data.id),
          email: data.email,
          name: data.name,
          picture: data.avatar_url,
          emailVerified: true
        }

      case 'microsoft':
        return {
          sub: data.id,
          email: data.userPrincipalName || data.mail,
          name: data.displayName,
          picture: undefined,
          emailVerified: true
        }

      case 'discord':
        return {
          sub: data.id,
          email: data.email,
          name: data.username,
          picture: data.avatar ? `https://cdn.discordapp.com/avatars/${data.id}/${data.avatar}.png` : undefined,
          emailVerified: data.verified
        }

      default:
        // Generic OIDC profile normalization
        return {
          sub: data.sub || data.id,
          email: data.email,
          name: data.name,
          picture: data.picture,
          emailVerified: data.email_verified
        }
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(
    provider: AuthProvider,
    refreshToken: string
  ): Promise<OAuthTokenResponse> {
    if (!provider.clientId || !provider.clientSecret) {
      throw new Error(`Provider ${provider.id} missing credentials`)
    }

    const providerConfig = PROVIDER_CONFIGS[provider.id]
    if (!providerConfig) {
      throw new Error(`Unknown OAuth provider: ${provider.id}`)
    }

    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: provider.clientId,
      client_secret: provider.clientSecret
    })

    const response = await fetch(providerConfig.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: params.toString()
    })

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.statusText}`)
    }

    const data = await response.json()

    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token || refreshToken,
      idToken: data.id_token,
      expiresIn: data.expires_in || 3600,
      tokenType: data.token_type || 'Bearer',
      scope: data.scope
    }
  }

  /**
   * Cleanup expired OAuth states
   */
  private cleanupExpiredStates(): void {
    const now = new Date()
    for (const [state, oauthState] of this.stateMap.entries()) {
      if (oauthState.expiresAt < now) {
        this.stateMap.delete(state)
      }
    }
  }

  /**
   * Get stored state for debugging
   */
  getState(state: string): OAuthState | undefined {
    return this.stateMap.get(state)
  }
}
